import React, { useMemo, useState } from "react";
import pricingData from "../../data/pricing.json";
import {
  isNotBlank,
  isInteger,
  isE164Phone,
  luhnCheck,
  isValidExpiry,
  isValidCVC
} from "../../utilities/InLine_validations";
import { useSecurity } from "../security/Security_store";

const STORAGE_PAYMENT_STATUS = "payment.status.v1";
const TwoColRow = ({ left, right }) => (
  <div className="row g-2 mb-3 align-items-start">
    <div className="col-12 col-md-6">{left}</div>
    <div className="col-12 col-md-6">{right}</div>
  </div>
);

const Account_Details = () => {
  const { userName, keyId, sessionToken } = useSecurity(); // GUID now; Cognito later

  // --- payer profile fields ---
  const [profile, setProfile] = useState({
    fullName: "",
    address1: "",
    address2: "",
    houseNumber: "",
    city: "",
    postcode: "",
    country: "",
    phoneE164: ""
  });

  // --- payment method + card fields ---
  const [method, setMethod] = useState("card"); // 'card' | 'paypal' | 'crypto'
  const [card, setCard] = useState({
    cardholder: "",
    number: "",
    expiryMonth: "",
    expiryYear: "",
    cvc: ""
  });

  // --- plan selection ---
  const [interval, setInterval] = useState("monthly"); // 'weekly' | 'monthly' | 'yearly'
  const [seats, setSeats] = useState("1");

  const currency = pricingData.currency || "GBP";
  // Pricing model:
  // - weekly = (baseMonthly * (1 - disc.weekly)) / 4
  // - monthly = baseMonthly * (1 - disc.monthly)
  // - yearly = (baseMonthly * 12) * (1 - disc.yearly)
  const price = useMemo(() => {
    const baseMonthly = Number(pricingData.baseMonthly || 0);
    const d = pricingData.discounts || {};
    let perUnit = 0;
    if (interval === "weekly") {
      perUnit = (baseMonthly * (1 - (d.weekly || 0))) / 4;
    } else if (interval === "monthly") {
      perUnit = baseMonthly * (1 - (d.monthly || 0));
    } else {
      perUnit = baseMonthly * 12 * (1 - (d.yearly || 0));
    }
    const seatCount = Math.max(1, parseInt(seats || "1", 10) || 1);
    return {
      perUnit: Number(perUnit.toFixed(2)),
      total: Number((perUnit * seatCount).toFixed(2)),
      seatCount
    };
  }, [interval, seats]);

  // --- PayPal / Crypto placeholders (wire later) ---
  const [paypalConfig] = useState({
    clientId: "PAYPAL-CLIENT-ID-PLACEHOLDER", // TODO: real client id
    env: "sandbox"
  });
  const [cryptoWallet] = useState({
    address: "0xDEADBEEFDEADBEEFDEADBEEFDEADBEEFDEADBEEF", // TODO: your wallet
    network: "ETH"
  });

  // --- helpers ---
  const update = (setter) => (e) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  // --- validations ---
  const [errors, setErrors] = useState({});

  const validateProfile = () => {
    const e = {};
    const p = profile;

    if (!isNotBlank(p.fullName)) e.fullName = "Full name is required.";
    if (!isNotBlank(p.address1)) e.address1 = "Address line 1 is required.";
    if (!isNotBlank(p.city)) e.city = "City/Town is required.";
    if (!isNotBlank(p.postcode)) e.postcode = "Postcode is required.";
    if (!isNotBlank(p.country)) e.country = "Country is required.";
    if (!isNotBlank(p.houseNumber) || !isInteger(p.houseNumber)) e.houseNumber = "House number must be an integer.";
    if (!isNotBlank(p.phoneE164) || !isE164Phone(p.phoneE164)) e.phoneE164 = "Phone must be E.164, e.g. +447911123456.";

    return e;
  };

  const detectBrand = (pan) => {
    const s = (pan || "").replace(/\s+/g, "");
    if (/^4\d{0,}/.test(s)) return "visa";
    if (/^5[1-5]\d{0,}/.test(s)) return "mastercard";
    if (/^3[47]\d{0,}/.test(s)) return "amex";
    if (/^6(?:011|5)\d{0,}/.test(s)) return "discover";
    return "generic";
  };

  const validateCard = () => {
    if (method !== "card") return {};
    const e = {};
    const c = card;
    const brand = detectBrand(c.number);

    const num = (c.number || "").replace(/\s+/g, "");
    if (!isNotBlank(c.cardholder)) e.cardholder = "Cardholder is required.";
    if (!luhnCheck(num)) e.number = "Invalid card number.";
    if (!isValidExpiry(c.expiryMonth, c.expiryYear)) e.expiry = "Invalid expiry (MM/YY) in the future.";
    if (!isValidCVC(c.cvc, brand)) e.cvc = "Invalid CVC.";

    return e;
  };

  const validateAll = () => {
    const e1 = validateProfile();
    const e2 = validateCard();
    const e = { ...e1, ...e2 };
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // --- local helper to mark payment on file & advance Members flow ---
  const markPaidAndAdvance = (provider) => {
    localStorage.setItem(
      STORAGE_PAYMENT_STATUS,
      JSON.stringify({ hasMethod: true, provider, lastUpdated: new Date().toISOString() })
    );
    if (window.__MOCK__PAYMENT_OK) window.__MOCK__PAYMENT_OK(provider);
  };

  // --- submit handlers (mock gateways) ---
  const submitCard = (evt) => {
    evt.preventDefault();
    if (!validateAll()) return;

    // In live: call your backend → Stripe Payment Intent with amount = price.total, currency, metadata
    console.log("MOCK STRIPE CHARGE", {
      userName,
      keyId,
      sessionToken,
      amount: price.total,
      currency,
      interval,
      seats: price.seatCount,
      profile,
      card: { ...card, number: "***masked***" }
    });

    alert(`Card payment (mock) for ${currency} ${price.total} accepted.`);
    markPaidAndAdvance("card");
  };

  const submitPayPal = (evt) => {
    evt.preventDefault();
    const e = validateProfile();
    setErrors(e);
    if (Object.keys(e).length) return;

    // In live: render PayPal Buttons SDK with paypalConfig.clientId, createOrder → capture
    console.log("MOCK PAYPAL", {
      userName,
      keyId,
      sessionToken,
      amount: price.total,
      currency,
      interval,
      seats: price.seatCount,
      profile,
      paypalConfig
    });
    alert(`PayPal (mock) for ${currency} ${price.total}.`);
    markPaidAndAdvance("paypal");
  };

  const submitCrypto = (evt) => {
    evt.preventDefault();
    const e = validateProfile();
    setErrors(e);
    if (Object.keys(e).length) return;

    // In live: display QR, wait for on-chain confirmation to cryptoWallet.address on cryptoWallet.network
    console.log("MOCK CRYPTO", {
      userName,
      keyId,
      sessionToken,
      amount: price.total,
      currency,
      interval,
      seats: price.seatCount,
      profile,
      cryptoWallet
    });
    alert(`Send ${currency} ${price.total} equivalent to ${cryptoWallet.address} on ${cryptoWallet.network}.`);
    markPaidAndAdvance("crypto");
  };

  // UI helpers
  const priceLine = useMemo(() => {
    const b = pricingData.baseMonthly || 0;
    const d = pricingData.discounts || {};
    const fmt = (v) => `${currency} ${v.toFixed(2)}`;
    const weekly = (b * (1 - (d.weekly || 0))) / 4;
    const monthly = b * (1 - (d.monthly || 0));
    const yearly = b * 12 * (1 - (d.yearly || 0));
    return { weekly, monthly, yearly, fmt };
  }, [currency]);

  // keep two-column look
  return (
    <div className="container-fluid py-3">
      <h1 className="h4 mb-3">Account & Payment</h1>
      <p className="text-muted mb-4">
        Signed in as <strong>{userName || "Unknown"}</strong>. KeyID <code>{keyId || "(none)"}</code>
      </p>

      {/* Plan selection */}
      <div className="border rounded p-3 mb-4">
        <TwoColRow
          left={<strong>Choose billing interval</strong>}
          right={
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-${interval === "weekly" ? "primary" : "outline-primary"}`}
                onClick={() => setInterval("weekly")}
              >
                Weekly ({priceLine.fmt(priceLine.weekly)})
              </button>
              <button
                type="button"
                className={`btn btn-${interval === "monthly" ? "primary" : "outline-primary"}`}
                onClick={() => setInterval("monthly")}
              >
                Monthly ({priceLine.fmt(priceLine.monthly)})
              </button>
              <button
                type="button"
                className={`btn btn-${interval === "yearly" ? "primary" : "outline-primary"}`}
                onClick={() => setInterval("yearly")}
              >
                Yearly ({priceLine.fmt(priceLine.yearly)})
              </button>
            </div>
          }
        />

        <TwoColRow
          left={<label className="col-form-label" htmlFor="seats">Number of users (seats)</label>}
          right={
            <input
              id="seats"
              name="seats"
              className="form-control"
              value={seats}
              onChange={(e) => setSeats(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="1"
              inputMode="numeric"
            />
          }
        />

        <TwoColRow
          left={<span className="text-muted">Price</span>}
          right={
            <div>
              <div>
                <strong>Total: {currency} {price.total.toFixed(2)}</strong>{" "}
                <small className="text-muted">({price.seatCount} seat{price.seatCount > 1 ? "s" : ""})</small>
              </div>
            </div>
          }
        />
      </div>

      {/* Profile form */}
      <div className="border rounded p-3 mb-4">
        <h2 className="h5 mb-3">Billing details</h2>

        <TwoColRow
          left={<label htmlFor="fullName" className="col-form-label">Full name</label>}
          right={
            <div>
              <input id="fullName" name="fullName" className="form-control" value={profile.fullName} onChange={update(setProfile)} />
              {errors.fullName && <small className="text-danger">{errors.fullName}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="address1" className="col-form-label">Address line 1</label>}
          right={
            <div>
              <input id="address1" name="address1" className="form-control" value={profile.address1} onChange={update(setProfile)} />
              {errors.address1 && <small className="text-danger">{errors.address1}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="address2" className="col-form-label">Address line 2 (optional)</label>}
          right={
            <input id="address2" name="address2" className="form-control" value={profile.address2} onChange={update(setProfile)} />
          }
        />

        <TwoColRow
          left={<label htmlFor="houseNumber" className="col-form-label">House number (integer)</label>}
          right={
            <div>
              <input
                id="houseNumber"
                name="houseNumber"
                className="form-control"
                value={profile.houseNumber}
                onChange={update(setProfile)}
                inputMode="numeric"
                placeholder="e.g. 12"
              />
              {errors.houseNumber && <small className="text-danger">{errors.houseNumber}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="city" className="col-form-label">City / Town</label>}
          right={
            <div>
              <input id="city" name="city" className="form-control" value={profile.city} onChange={update(setProfile)} />
              {errors.city && <small className="text-danger">{errors.city}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="postcode" className="col-form-label">Postcode</label>}
          right={
            <div>
              <input id="postcode" name="postcode" className="form-control" value={profile.postcode} onChange={update(setProfile)} />
              {errors.postcode && <small className="text-danger">{errors.postcode}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="country" className="col-form-label">Country</label>}
          right={
            <div>
              <input id="country" name="country" className="form-control" value={profile.country} onChange={update(setProfile)} />
              {errors.country && <small className="text-danger">{errors.country}</small>}
            </div>
          }
        />

        <TwoColRow
          left={<label htmlFor="phoneE164" className="col-form-label">Telephone (E.164)</label>}
          right={
            <div>
              <input
                id="phoneE164"
                name="phoneE164"
                className="form-control"
                value={profile.phoneE164}
                onChange={update(setProfile)}
                placeholder="+447911123456"
              />
              {errors.phoneE164 && <small className="text-danger">{errors.phoneE164}</small>}
            </div>
          }
        />
      </div>

      {/* Payment method choice */}
      <div className="border rounded p-3">
        <h2 className="h5 mb-3">Payment method</h2>

        <TwoColRow
          left={<span className="text-muted">Method</span>}
          right={
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-${method === "card" ? "primary" : "outline-primary"}`}
                onClick={() => setMethod("card")}
              >
                Credit / Debit Card
              </button>
              <button
                type="button"
                className={`btn btn-${method === "paypal" ? "primary" : "outline-primary"}`}
                onClick={() => setMethod("paypal")}
              >
                PayPal
              </button>
              <button
                type="button"
                className={`btn btn-${method === "crypto" ? "primary" : "outline-primary"}`}
                onClick={() => setMethod("crypto")}
              >
                Crypto Wallet
              </button>
            </div>
          }
        />

        {method === "card" && (
          <>
            <TwoColRow
              left={<label htmlFor="cardholder" className="col-form-label">Cardholder name</label>}
              right={
                <div>
                  <input id="cardholder" name="cardholder" className="form-control" value={card.cardholder} onChange={update(setCard)} />
                  {errors.cardholder && <small className="text-danger">{errors.cardholder}</small>}
                </div>
              }
            />
            <TwoColRow
              left={<label htmlFor="number" className="col-form-label">Card number</label>}
              right={
                <div>
                  <input id="number" name="number" className="form-control" value={card.number} onChange={update(setCard)} placeholder="4242 4242 4242 4242" />
                  {errors.number && <small className="text-danger">{errors.number}</small>}
                </div>
              }
            />
            <TwoColRow
              left={<label className="col-form-label">Expiry (MM / YY)</label>}
              right={
                <div className="d-flex gap-2">
                  <input name="expiryMonth" className="form-control" style={{ maxWidth: 100 }} value={card.expiryMonth} onChange={update(setCard)} placeholder="MM" inputMode="numeric" />
                  <input name="expiryYear" className="form-control" style={{ maxWidth: 100 }} value={card.expiryYear} onChange={update(setCard)} placeholder="YY" inputMode="numeric" />
                </div>
              }
            />
            {errors.expiry && (
              <div className="row g-2 mb-3">
                <div className="col-12 col-md-6"></div>
                <div className="col-12 col-md-6"><small className="text-danger">{errors.expiry}</small></div>
              </div>
            )}
            <TwoColRow
              left={<label htmlFor="cvc" className="col-form-label">CVC</label>}
              right={
                <div>
                  <input id="cvc" name="cvc" className="form-control" style={{ maxWidth: 150 }} value={card.cvc} onChange={update(setCard)} inputMode="numeric" placeholder="3 or 4 digits" />
                  {errors.cvc && <small className="text-danger">{errors.cvc}</small>}
                </div>
              }
            />

            <div className="row g-2">
              <div className="col-12 col-md-6"></div>
              <div className="col-12 col-md-6">
                <button className="btn btn-primary" onClick={submitCard}>
                  Pay {currency} {price.total.toFixed(2)}
                </button>
              </div>
            </div>
          </>
        )}

        {method === "paypal" && (
          <>
            <TwoColRow
              left={<span className="text-muted">PayPal</span>}
              right={
                <div className="text-muted">
                  Client ID: <code>{paypalConfig.clientId}</code> ({paypalConfig.env})
                </div>
              }
            />

            <div className="row g-2">
              <div className="col-12 col-md-6"></div>
              <div className="col-12 col-md-6">
                <button className="btn btn-primary" onClick={submitPayPal}>
                  Continue with PayPal — {currency} {price.total.toFixed(2)}
                </button>
              </div>
            </div>
          </>
        )}

        {method === "crypto" && (
          <>
            <TwoColRow
              left={<span className="text-muted">Wallet</span>}
              right={
                <div>
                  <div className="mb-1">Network: <strong>{cryptoWallet.network}</strong></div>
                  <div className="mb-1">Address: <code className="user-select-all">{cryptoWallet.address}</code></div>
                  <small className="text-muted">Send equivalent of {currency} {price.total.toFixed(2)} and then return to complete.</small>
                </div>
              }
            />
            <div className="row g-2">
              <div className="col-12 col-md-6"></div>
              <div className="col-12 col-md-6">
                <button className="btn btn-primary" onClick={submitCrypto}>
                  I’ve sent the funds
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Account_Details;